import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, Eye, Database, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#0A091E] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A091E]/90 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0A091E]" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              SRM <span className="text-[#D4AF37]">Cadence</span>
            </span>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Privacy <span className="text-[#D4AF37]">Policy</span>
        </h1>
        <p className="text-white/50 mb-8">Last Updated: February 21, 2026</p>

        {/* Security Highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="glass-card p-4 rounded-xl text-center">
            <Lock className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-sm text-white/70">AES-256 Encryption</p>
          </div>
          <div className="glass-card p-4 rounded-xl text-center">
            <Eye className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-sm text-white/70">Zero-Trust Security</p>
          </div>
          <div className="glass-card p-4 rounded-xl text-center">
            <Database className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-sm text-white/70">GDPR Compliant</p>
          </div>
          <div className="glass-card p-4 rounded-xl text-center">
            <Bell className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-sm text-white/70">Opt-Out Available</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">1. Introduction</h2>
            <p className="text-white/70 leading-relaxed">
              SRM Cadence ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your information when you use our workforce management platform ("Service"). 
              Please read this policy carefully. By using the Service, you consent to the practices described in this policy.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-2">Personal Information</h3>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Name, email address, and contact information</li>
              <li>Phone number (for MFA and notifications)</li>
              <li>Employment information (role, department, contract rate)</li>
              <li>Authentication credentials (encrypted)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Usage Information</h3>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Time tracking data (clock in/out, breaks, lunch)</li>
              <li>Attendance records and patterns</li>
              <li>Performance metrics and KPIs</li>
              <li>Leave requests and shift swap history</li>
              <li>File uploads and downloads in the Secure Dropzone</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Technical Information</h3>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Access timestamps and session data</li>
              <li>Audit trail logs for compliance</li>
            </ul>
          </section>

          <section className="glass-card p-6 rounded-xl border-l-4 border-l-[#D4AF37]">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">3. Phone Number and SMS/Voice Communications</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-2">How We Use Your Phone Number</h3>
            <p className="text-white/70 leading-relaxed mb-4">
              When you provide your phone number during registration, we use it for the following purposes:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li><strong className="text-white">Multi-Factor Authentication (MFA):</strong> Sending verification codes via SMS or automated voice calls to secure your account</li>
              <li><strong className="text-white">Shift Reminders:</strong> Sending "Wake Up" alerts before your scheduled shift start time</li>
              <li><strong className="text-white">Time-Sensitive Notifications:</strong> Alerting you to schedule changes, approvals, and urgent matters</li>
              <li><strong className="text-white">Performance Recognition:</strong> Notifying you of achievements and accolades</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Your Consent</h3>
            <p className="text-white/70 leading-relaxed">
              By providing your phone number and agreeing to our Terms of Service, you explicitly consent to receive SMS messages 
              and automated voice calls from SRM Cadence. This consent is required for account security (MFA) and is optional for 
              other notification types.
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Opting Out</h3>
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">To stop receiving SMS messages:</strong> Reply STOP to any message from us, or update your 
              notification preferences in your account settings. Note that opting out of MFA messages will require you to use an 
              authenticator app instead.
            </p>
            <p className="text-white/70 leading-relaxed mt-2">
              <strong className="text-white">For help:</strong> Reply HELP to any message or contact support@srmcadence.com.
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Message Frequency and Costs</h3>
            <p className="text-white/70 leading-relaxed">
              Message frequency varies based on your work schedule and notification preferences. Standard message and data rates 
              from your mobile carrier may apply. We do not charge for SMS or voice communications.
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">Third-Party Service Provider</h3>
            <p className="text-white/70 leading-relaxed">
              We use Twilio, a trusted third-party communications provider, to deliver SMS and voice communications. Twilio processes 
              your phone number solely for the purpose of delivering our messages and does not use your information for marketing 
              or other purposes. For more information, see Twilio's privacy policy at twilio.com/legal/privacy.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">4. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process time tracking and payroll calculations</li>
              <li>Authenticate your identity and secure your account</li>
              <li>Send notifications and communications you've consented to</li>
              <li>Generate reports for management and compliance</li>
              <li>Maintain audit trails for ISO/GDPR compliance</li>
              <li>Detect, prevent, and address security issues</li>
            </ul>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">5. Data Security</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We implement enterprise-grade security measures to protect your data:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li><strong className="text-white">AES-256 Encryption:</strong> All sensitive data is encrypted at rest and in transit</li>
              <li><strong className="text-white">Zero-Trust Architecture:</strong> Multi-layer authentication with MFA requirements</li>
              <li><strong className="text-white">Access Controls:</strong> Role-based permissions limiting data access</li>
              <li><strong className="text-white">Audit Logging:</strong> Comprehensive logging of all system access and changes</li>
              <li><strong className="text-white">Secure Infrastructure:</strong> Enterprise-grade cloud hosting with regular security audits</li>
            </ul>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-white/70 leading-relaxed mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li><strong className="text-white">Your Employer:</strong> Time tracking, attendance, and performance data as part of the Service</li>
              <li><strong className="text-white">Service Providers:</strong> Twilio (communications), SendGrid (email), and cloud infrastructure providers</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-white/70 leading-relaxed mt-4">
              We do not sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">7. Data Retention</h2>
            <p className="text-white/70 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide the Service. 
              We also retain data as necessary to comply with legal obligations, resolve disputes, and enforce agreements. 
              Audit trail data is retained for a minimum of 7 years for compliance purposes.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">8. Your Rights (GDPR)</h2>
            <p className="text-white/70 leading-relaxed mb-4">Under GDPR and similar regulations, you have the right to:</p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong className="text-white">Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong className="text-white">Restriction:</strong> Request limitation of processing</li>
              <li><strong className="text-white">Portability:</strong> Request transfer of your data</li>
              <li><strong className="text-white">Objection:</strong> Object to certain types of processing</li>
            </ul>
            <p className="text-white/70 leading-relaxed mt-4">
              To exercise these rights, contact privacy@srmcadence.com.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">9. Children's Privacy</h2>
            <p className="text-white/70 leading-relaxed">
              The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information 
              from children. If we become aware that we have collected data from a child, we will take steps to delete it.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">10. Changes to This Policy</h2>
            <p className="text-white/70 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy 
              on this page and updating the "Last Updated" date. For material changes, we will provide notice via email or 
              through the Service.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">11. Contact Us</h2>
            <p className="text-white/70 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="text-white mt-2">
              Privacy Inquiries: privacy@srmcadence.com<br />
              General Support: support@srmcadence.com<br />
              Legal Department: legal@srmcadence.com
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex justify-center gap-6">
          <Link to="/terms" className="text-[#D4AF37] hover:text-[#D4AF37]/80">
            Terms of Service
          </Link>
          <Link to="/login" className="text-white/50 hover:text-white">
            Back to Login
          </Link>
        </div>
      </main>
    </div>
  );
}
